import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { updateFormGate } from "../../../redux/actions/formsAction";
import { listSequences } from "../../../redux/actions/sequencesAction";
import { useFormDetail } from "./FormDetailContext";

const { Title, Text, Paragraph } = Typography;

// Mirrors mailinflow-apis/lib/forms/constants.js GATE_DEFAULT_CONFIG.
const GATE_BOOLEAN_FIELDS = [
  {
    key: "blockFreeMail",
    label: "Block free-mail addresses",
    hint: "Gmail, Yahoo, Outlook.com, etc.",
  },
  {
    key: "blockRoleAddresses",
    label: "Block role addresses",
    hint: "info@, sales@, admin@, support@, etc.",
  },
  {
    key: "blockDisposable",
    label: "Block disposable email addresses",
  },
  {
    key: "requireCompany",
    label: "Require a company name",
  },
  {
    key: "requireMx",
    label: "Require the email domain to have valid mail servers (MX)",
  },
];

// Mirrors mailinflow-apis/lib/forms/constants.js ROUTING_NOTIFY_CHANNELS/
// COLD_DIGEST_CADENCES -- kept in sync by hand, same as every other
// cross-repo vocabulary list in this app (see FormDpdp.js's PURPOSE_OPTIONS).
const NOTIFY_OPTIONS = [
  { value: "owner_email", label: "Email the form owner" },
  { value: "slack", label: "Slack (via the form's webhook)" },
  { value: "webhook", label: "Webhook (via the form's webhook)" },
  { value: "whatsapp", label: "WhatsApp (coming soon)" },
];

const DIGEST_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

const EMPTY_GATE = {
  blockFreeMail: false,
  blockedDomains: [],
  allowedDomains: [],
  requireCompany: false,
  blockRoleAddresses: false,
  blockDisposable: false,
  requireMx: false,
};

const EMPTY_TIERS = { hot: 75, warm: 45 };

const EMPTY_ROUTING = {
  hot: { tag: "", notify: [], sequenceId: undefined },
  warm: { tag: "", sequenceId: undefined },
  cold: { hold: false, digest: undefined },
};

export default function FormLeadScoring() {
  const { id, isNew, formRecord, reloadForm } = useFormDetail();
  const dispatch = useDispatch();
  const sequencesList = useSelector((state) => state.sequences.sequences);
  const [gate, setGate] = useState(EMPTY_GATE);
  const [tiers, setTiers] = useState(EMPTY_TIERS);
  const [routing, setRouting] = useState(EMPTY_ROUTING);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(listSequences({ status: "active" })).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!formRecord) return;
    const savedGate = formRecord.settings?.gate || {};
    setGate({ ...EMPTY_GATE, ...savedGate });
    setTiers({ ...EMPTY_TIERS, ...(savedGate.tiers || {}) });
    setRouting({
      hot: { ...EMPTY_ROUTING.hot, ...(savedGate.routing?.hot || {}) },
      warm: { ...EMPTY_ROUTING.warm, ...(savedGate.routing?.warm || {}) },
      cold: { ...EMPTY_ROUTING.cold, ...(savedGate.routing?.cold || {}) },
    });
  }, [formRecord]);

  if (isNew) {
    return (
      <Alert
        type="info"
        showIcon
        message="Save your form first"
        description="Go to Settings, name your form, and click Save. Then you can configure gate rules, lead scoring, and routing here."
      />
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateFormGate(id, {
          ...gate,
          tiers,
          routing: {
            hot: {
              ...(routing.hot.tag ? { tag: routing.hot.tag } : {}),
              ...(routing.hot.notify?.length
                ? { notify: routing.hot.notify }
                : {}),
              ...(routing.hot.sequenceId
                ? { sequenceId: routing.hot.sequenceId }
                : {}),
            },
            warm: {
              ...(routing.warm.tag ? { tag: routing.warm.tag } : {}),
              ...(routing.warm.sequenceId
                ? { sequenceId: routing.warm.sequenceId }
                : {}),
            },
            cold: {
              ...(routing.cold.hold ? { hold: true } : {}),
              ...(routing.cold.digest ? { digest: routing.cold.digest } : {}),
            },
          },
        }),
      );
      message.success("Gate, scoring and routing settings saved.");
      reloadForm();
    } catch (err) {
      // privatePutApi already surfaces the server's error message as a toast.
    } finally {
      setSaving(false);
    }
  };

  const sequenceOptions = (sequencesList || []).map((s) => ({
    value: s._id,
    label: s.name,
  }));

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={5} style={{ marginTop: 0 }}>
          Gate &amp; lead scoring
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Qualify submissions before they're accepted, and route them by lead
          quality once they are.
        </Paragraph>
      </div>

      <Card size="small" title="Submission gate">
        <Paragraph type="secondary">
          A blocked submission sees a visitor-facing error instead of being
          silently accepted. Leave everything off to accept all submissions
          (the default).
        </Paragraph>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {GATE_BOOLEAN_FIELDS.map((field) => (
            <Space key={field.key} align="center">
              <Switch
                checked={!!gate[field.key]}
                onChange={(checked) =>
                  setGate((prev) => ({ ...prev, [field.key]: checked }))
                }
              />
              <div>
                <Text>{field.label}</Text>
                {field.hint && (
                  <>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {field.hint}
                    </Text>
                  </>
                )}
              </div>
            </Space>
          ))}

          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">Blocked domains</Text>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                value={gate.blockedDomains}
                onChange={(v) =>
                  setGate((prev) => ({ ...prev, blockedDomains: v }))
                }
                placeholder="e.g. competitor.com"
                tokenSeparators={[",", " "]}
              />
            </Col>
            <Col span={12}>
              <Text type="secondary">
                Allowed domains (if set, only these are accepted)
              </Text>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                value={gate.allowedDomains}
                onChange={(v) =>
                  setGate((prev) => ({ ...prev, allowedDomains: v }))
                }
                placeholder="e.g. yourclient.com"
                tokenSeparators={[",", " "]}
              />
            </Col>
          </Row>
        </Space>
      </Card>

      <Card size="small" title="Lead score tiers">
        <Paragraph type="secondary">
          Every non-spam submission gets a 0–100 lead score. A submission at
          or above the hot threshold is "hot"; at or above the warm
          threshold (but below hot) is "warm"; anything else is "cold".
        </Paragraph>
        <Space size="large" wrap>
          <div>
            <Text type="secondary">Hot threshold</Text>
            <br />
            <InputNumber
              min={tiers.warm + 1}
              max={100}
              value={tiers.hot}
              onChange={(v) =>
                setTiers((prev) => ({ ...prev, hot: v ?? prev.hot }))
              }
            />
          </div>
          <div>
            <Text type="secondary">Warm threshold</Text>
            <br />
            <InputNumber
              min={0}
              max={tiers.hot - 1}
              value={tiers.warm}
              onChange={(v) =>
                setTiers((prev) => ({ ...prev, warm: v ?? prev.warm }))
              }
            />
          </div>
        </Space>
      </Card>

      <Card size="small" title="Routing">
        <Paragraph type="secondary">
          What happens automatically once a submission is scored and
          promoted. Leave a tier's fields empty to take no automatic action
          for it.
        </Paragraph>

        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <Text strong>Hot leads</Text>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={8}>
                <Text type="secondary">Tag to apply</Text>
                <Select
                  mode="tags"
                  maxCount={1}
                  style={{ width: "100%" }}
                  value={routing.hot.tag ? [routing.hot.tag] : []}
                  onChange={(v) =>
                    setRouting((prev) => ({
                      ...prev,
                      hot: { ...prev.hot, tag: v[v.length - 1] || "" },
                    }))
                  }
                  placeholder="e.g. hot-lead"
                />
              </Col>
              <Col span={8}>
                <Text type="secondary">Notify</Text>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  value={routing.hot.notify}
                  options={NOTIFY_OPTIONS}
                  onChange={(v) =>
                    setRouting((prev) => ({
                      ...prev,
                      hot: { ...prev.hot, notify: v },
                    }))
                  }
                />
              </Col>
              <Col span={8}>
                <Text type="secondary">Enroll in sequence</Text>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  value={routing.hot.sequenceId}
                  options={sequenceOptions}
                  onChange={(v) =>
                    setRouting((prev) => ({
                      ...prev,
                      hot: { ...prev.hot, sequenceId: v },
                    }))
                  }
                  placeholder="None"
                />
              </Col>
            </Row>
          </div>

          <div>
            <Text strong>Warm leads</Text>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={12}>
                <Text type="secondary">Tag to apply</Text>
                <Select
                  mode="tags"
                  maxCount={1}
                  style={{ width: "100%" }}
                  value={routing.warm.tag ? [routing.warm.tag] : []}
                  onChange={(v) =>
                    setRouting((prev) => ({
                      ...prev,
                      warm: { ...prev.warm, tag: v[v.length - 1] || "" },
                    }))
                  }
                  placeholder="e.g. warm-lead"
                />
              </Col>
              <Col span={12}>
                <Text type="secondary">Enroll in sequence</Text>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  value={routing.warm.sequenceId}
                  options={sequenceOptions}
                  onChange={(v) =>
                    setRouting((prev) => ({
                      ...prev,
                      warm: { ...prev.warm, sequenceId: v },
                    }))
                  }
                  placeholder="None"
                />
              </Col>
            </Row>
          </div>

          <div>
            <Text strong>Cold leads</Text>
            <Row gutter={16} style={{ marginTop: 8 }} align="middle">
              <Col span={12}>
                <Space align="center">
                  <Switch
                    checked={!!routing.cold.hold}
                    onChange={(checked) =>
                      setRouting((prev) => ({
                        ...prev,
                        cold: { ...prev.cold, hold: checked },
                      }))
                    }
                  />
                  <Text>
                    Hold instead of promoting (surfaced in a digest below)
                  </Text>
                </Space>
              </Col>
              <Col span={12}>
                <Text type="secondary">Digest frequency</Text>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  value={routing.cold.digest}
                  options={DIGEST_OPTIONS}
                  onChange={(v) =>
                    setRouting((prev) => ({
                      ...prev,
                      cold: { ...prev.cold, digest: v },
                    }))
                  }
                  placeholder="No digest"
                />
              </Col>
            </Row>
          </div>
        </Space>
      </Card>

      <Button type="primary" loading={saving} onClick={save}>
        Save
      </Button>
    </Space>
  );
}
